import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { formsApi, getErrorMessage } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  ClipboardEdit, ClipboardList, CheckCircle2, Clock, AlertTriangle, FileCheck2,
  RefreshCw, Inbox, TrendingUp, ArrowRight, Eye, Mail, Send,
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

/**
 * Forms-workspace dashboard. Sister to `DashboardPage` (Documents)
 * but driven by `GET /api/forms/dashboard/stats`. Shares the same
 * neumorphic / StatCard visual language so a recruiter flipping
 * workspaces feels at home immediately.
 */

const StatCard = ({ icon: Icon, label, value, color, delay, onClick, testId }) => (
  <div
    className={`neumorphic rounded-xl p-6 animate-fade-in opacity-0 stagger-${delay} ${onClick ? 'cursor-pointer card-hover transition-transform hover:-translate-y-0.5' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
    data-testid={testId || `forms-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="overline mb-2">{label}</p>
        <p className="font-heading text-3xl font-bold text-foreground">{value}</p>
      </div>
      <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const STATUS_BADGES = {
  sent: { label: 'Sent', class: 'bg-sky-100 text-sky-700' },
  opened: { label: 'Opened', class: 'bg-amber-100 text-amber-700' },
  in_progress: { label: 'In Progress', class: 'bg-amber-100 text-amber-700' },
  completed: { label: 'Completed', class: 'bg-emerald-100 text-emerald-700' },
  expired: { label: 'Expired', class: 'bg-rose-100 text-rose-700' },
};

export default function FormsDashboardPage() {
  const { tenant, getTenantPath } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const { data } = await formsApi.dashboardStats();
      setStats(data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Online Forms is not enabled for this workspace');
        navigate(getTenantPath('/dashboard'));
        return;
      }
      toast.error(getErrorMessage(err, 'Failed to load Forms dashboard'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate, getTenantPath]);

  useEffect(() => { load(); }, [load]);

  if (loading || !stats) {
    return (
      <DashboardLayout>
        <div className="p-8 text-center text-muted-foreground">Loading Forms dashboard…</div>
      </DashboardLayout>
    );
  }

  const subs = stats.submissions || {};
  const forms = stats.forms || {};
  const sync = stats.sync || {};

  return (
    <DashboardLayout>
      <div className="p-8 max-w-7xl mx-auto" data-testid="forms-dashboard-page">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <p className="overline text-violet-600">Forms workspace</p>
            <h1 className="font-heading text-4xl text-foreground tracking-tight mt-1">
              Welcome back, {tenant?.name || 'team'}
            </h1>
            <p className="text-base text-muted-foreground mt-1">
              Track form sends, candidate responses, and Bullhorn write-back at a glance.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={refreshing} className="gap-1.5" data-testid="forms-dashboard-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button size="sm" onClick={() => navigate(getTenantPath('/forms'))} className="gap-1.5" data-testid="forms-dashboard-go-forms-btn">
              <ClipboardEdit className="w-3.5 h-3.5" />
              Manage Forms
            </Button>
          </div>
        </div>

        {/* Top KPI row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={ClipboardEdit}
            label="Total Forms"
            value={forms.total || 0}
            color="bg-gradient-to-br from-violet-500 to-fuchsia-500"
            delay={1}
            onClick={() => navigate(getTenantPath('/forms'))}
            testId="forms-stat-total-forms"
          />
          <StatCard
            icon={Inbox}
            label="Submissions"
            value={subs.total || 0}
            color="bg-gradient-to-br from-sky-500 to-blue-600"
            delay={2}
            onClick={() => navigate(getTenantPath('/forms/submissions'))}
            testId="forms-stat-submissions"
          />
          <StatCard
            icon={CheckCircle2}
            label="Completed"
            value={subs.completed || 0}
            color="bg-gradient-to-br from-emerald-500 to-teal-600"
            delay={3}
            onClick={() => navigate(getTenantPath('/forms/submissions') + '?status=completed')}
            testId="forms-stat-completed"
          />
          <StatCard
            icon={TrendingUp}
            label="Completion Rate"
            value={`${stats.completion_rate ?? 0}%`}
            color="bg-gradient-to-br from-amber-500 to-orange-500"
            delay={4}
            testId="forms-stat-completion-rate"
          />
        </div>

        {/* Status pipeline row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Send} label="Sent" value={subs.sent || 0} color="bg-sky-500" delay={1} onClick={() => navigate(getTenantPath('/forms/submissions') + '?status=sent')} />
          <StatCard icon={Eye} label="Opened" value={subs.opened || 0} color="bg-amber-500" delay={2} onClick={() => navigate(getTenantPath('/forms/submissions') + '?status=opened')} />
          <StatCard icon={Clock} label="In Progress" value={subs.in_progress || 0} color="bg-amber-500" delay={3} />
          <StatCard icon={AlertTriangle} label="Expired" value={subs.expired || 0} color="bg-rose-500" delay={4} />
        </div>

        {/* Bullhorn sync + Top forms — two columns */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
          {/* Bullhorn sync card */}
          <div className="neumorphic rounded-xl p-6" data-testid="forms-dashboard-bh-sync">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center">
                  <FileCheck2 className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-heading text-lg">Bullhorn Sync</h3>
              </div>
            </div>
            <div className="space-y-2 text-sm">
              <SyncRow label="Synced" count={sync.success || 0} class="text-emerald-700 bg-emerald-50" />
              <SyncRow label="Partial" count={sync.partial || 0} class="text-amber-700 bg-amber-50" />
              <SyncRow label="Failed" count={sync.failed || 0} class="text-rose-700 bg-rose-50" />
            </div>
            {(sync.failed > 0) && (
              <Button
                variant="ghost"
                size="sm"
                className="mt-4 w-full text-rose-700"
                onClick={() => navigate(getTenantPath('/forms/submissions'))}
              >
                Review failed sync{sync.failed > 1 ? 's' : ''}
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            )}
          </div>

          {/* Top forms */}
          <div className="neumorphic rounded-xl p-6 lg:col-span-2" data-testid="forms-dashboard-top-forms">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-fuchsia-500 to-pink-500 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-heading text-lg">Top Forms by Volume</h3>
              </div>
            </div>
            {stats.top_forms?.length ? (
              <div className="space-y-2">
                {stats.top_forms.map((f, idx) => (
                  <button
                    key={f.form_id}
                    type="button"
                    onClick={() => navigate(getTenantPath(`/forms/${f.form_id}/submissions`))}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors text-left"
                    data-testid={`forms-dashboard-top-form-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-xs font-mono text-muted-foreground w-5">#{idx + 1}</span>
                      <span className="font-medium text-sm truncate">{f.form_name}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-muted-foreground">{f.count} submission{f.count === 1 ? '' : 's'}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">No form submissions yet.</p>
            )}
          </div>
        </div>

        {/* Recent activity */}
        <div className="neumorphic rounded-xl p-6" data-testid="forms-dashboard-recent">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg">Recent activity</h3>
            <Button variant="ghost" size="sm" onClick={() => navigate(getTenantPath('/forms/submissions'))} className="gap-1.5">
              View all <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
          {stats.recent_activity?.length ? (
            <div className="space-y-2">
              {stats.recent_activity.map((s) => {
                const badge = STATUS_BADGES[s.status] || STATUS_BADGES.sent;
                return (
                  <div
                    key={s.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                    data-testid={`forms-dashboard-activity-${s.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-4 h-4 text-slate-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{s.candidate_name}</p>
                      <p className="text-xs text-muted-foreground truncate">{s.form_name}</p>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${badge.class}`}>
                      {badge.label}
                    </span>
                    <span className="text-xs text-muted-foreground w-24 text-right">
                      {s.sent_at ? formatDistanceToNow(new Date(s.sent_at), { addSuffix: true }) : '—'}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground italic">No activity yet.</p>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function SyncRow({ label, count, class: klass }) {
  return (
    <div className={`flex items-center justify-between px-3 py-2 rounded-lg ${klass}`}>
      <span className="font-medium">{label}</span>
      <span className="font-mono">{count}</span>
    </div>
  );
}
